import { datadogRum } from "@datadog/browser-rum";
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice
} from "@reduxjs/toolkit";

import { RootState } from "app/store";
import { removeSunoCommsCachedAuthData } from "services/suno-comms/utils/auth.utils";
import { Clinic, ClinicsResponse, Practice } from "services/types/clinics.type";
import { PaywallFeaturesStatus } from "services/types/paywall.type";
import { Region } from "services/types/regions.type";
import { MinimalStaffMember } from "services/types/staff.type";
import {
  AuthScheme,
  DEFAULT_AUTH_SCHEME,
  buildAuthHeaderValue
} from "utils/auth-header.util";
import { formatClinic } from "utils/clinics.util";
import { formatStaffMemberFullName } from "utils/common";
import {
  buildCriticalTimeoutSignal,
  isTimeoutAbort
} from "utils/request-timeouts";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";
import { viteEnv } from "utils/vite-env.util";

export const PRE_AUTH_USER_ID = -1;

export type UserSignupPayload = {
  uid?: string;
  token?: string;
  password: string;
};

export type UserLoginPayload = {
  email: string;
  password: string;
  keepLoggedIn: boolean;
};

export type UserForgotPasswordPayload = {
  email: string;
};

export type UserFetchPayload = {
  token?: string;
  uid?: string;
};

type AuthReturnType = {
  auth_token: string;
};

type RejectValueType = {
  errorMessage?: string;
};

export type CurrentUser = MinimalStaffMember & {
  default_clinic: Clinic | null;
  practice: Practice | null;
  comms_api_key?: string;
  clinics?: ClinicsResponse;
};

const EXPIRATION_TIME = 7 * 24 * 60 * 60 * 1000;

export const signupUser = createAsyncThunk<
  AuthReturnType,
  UserSignupPayload,
  { rejectValue: RejectValueType }
>(
  "auth/activate",
  async ({ uid, token, password }: UserSignupPayload, thunkAPI) => {
    try {
      const response = await fetch(
        `${viteEnv.VITE_SUNO_API}/auth/users/activation/`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            uid,
            token,
            password1: password
          })
        }
      );
      const data = await response.json();
      if (response.status.toString().match(/2\d{2}/)) {
        localStorage.setItem("token", data.auth_token);
        localStorage.setItem("authScheme", DEFAULT_AUTH_SCHEME);
        return data;
      } else {
        return thunkAPI.rejectWithValue({});
      }
    } catch (err: any) {
      const errorMessage =
        "Something went wrong. Please contact your support and describe your issue.";
      return thunkAPI.rejectWithValue({ errorMessage });
    }
  }
);

export const loginUser = createAsyncThunk<
  AuthReturnType,
  UserLoginPayload,
  { rejectValue: RejectValueType }
>("auth/login", async ({ email, password, keepLoggedIn }, thunkAPI) => {
  try {
    const response: any = await fetch(
      `${viteEnv.VITE_SUNO_API}/auth/token/login/`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email,
          password
        })
      }
    );
    const data = await response.json();
    if (response.status.toString().match(/2\d{2}/)) {
      if (keepLoggedIn) {
        const expirationDate = new Date(new Date().getTime() + EXPIRATION_TIME);
        const timestamp = expirationDate.getTime().toString();
        localStorage.setItem("token", data.auth_token);
        localStorage.setItem("expiration", timestamp);
        localStorage.setItem("authScheme", DEFAULT_AUTH_SCHEME);
      } else {
        sessionStorage.setItem("token", data.auth_token);
        sessionStorage.setItem("authScheme", DEFAULT_AUTH_SCHEME);
      }
      return data;
    } else {
      // Handle specific error codes from backend
      let errorMessage: string;
      if (data.error_code === "NO_PRACTICE_ASSIGNED") {
        errorMessage =
          data.detail ||
          "Your account is not assigned to any practice. Please contact your administrator.";
      } else if (data.error_code === "ACCOUNT_DEACTIVATED") {
        errorMessage =
          data.detail ||
          "Your account has been deactivated. Please contact your administrator.";
      } else {
        errorMessage =
          "Wrong email or password. Try again or select forgot password to reset.";
      }
      return thunkAPI.rejectWithValue({ errorMessage });
    }
  } catch (err: any) {
    const errorMessage =
      "Something went wrong. Please contact your support and describe your issue.";
    return thunkAPI.rejectWithValue({ errorMessage });
  }
});

// Atomic fail-closed cleanup for the SSO callback transaction (D3). The
// callback writes token+expiration BEFORE fetchCurrentUser runs, so any later
// failure (no practice, fetch error) must clear ALL auth — otherwise RequireAuth
// rehydrates a token with no usable session (the silent empty-practice state).
export const clearSsoSession = createAsyncThunk<boolean>(
  "auth/clearSsoSession",
  async () => {
    localStorage.removeItem("token");
    localStorage.removeItem("expiration");
    localStorage.removeItem("authScheme");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("authScheme");
    return true;
  }
);

export const resetUserPassword = createAsyncThunk<
  any,
  UserForgotPasswordPayload,
  { rejectValue: RejectValueType }
>("auth/resetUserPassword", async ({ email }, thunkAPI) => {
  try {
    return fetch(`${viteEnv.VITE_SUNO_API}/auth/users/reset_password/`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ email })
    });
  } catch (err: any) {
    const errorMessage =
      "Something went wrong. Please contact your support and describe your issue.";
    return thunkAPI.rejectWithValue({ errorMessage });
  }
});

export const resetUserPasswordConfirm = createAsyncThunk<
  any,
  UserSignupPayload,
  { rejectValue: RejectValueType }
>(
  "auth/resetUserPasswordConfirm",
  async ({ uid, token, password }: UserSignupPayload, thunkAPI) => {
    try {
      const response = await fetch(
        `${viteEnv.VITE_SUNO_API}/auth/users/reset_password_confirm/`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            uid,
            token,
            new_password: password
          })
        }
      );
      if (response.status.toString().match(/2\d{2}/)) {
        return response;
      } else {
        const errorMessage =
          "Something went wrong. Please contact your support and describe your issue.";
        return thunkAPI.rejectWithValue({ errorMessage });
      }
    } catch (err: any) {
      const errorMessage =
        "Something went wrong. Please contact your support and describe your issue.";
      return thunkAPI.rejectWithValue({ errorMessage });
    }
  }
);

const FETCH_USER_MAX_RETRIES = 2;
// Timeout aborts get a tighter budget than `Failed to fetch` so a hung /me on an
// unstable network can't stretch the bootstrap to FETCH_USER_MAX_RETRIES × the
// flag value. 1 retry → 2 attempts worst case.
const FETCH_USER_ABORT_MAX_RETRIES = 1;
const FETCH_USER_RETRY_DELAY_MS = 1000;

const isNetworkError = (err: unknown): boolean =>
  err instanceof TypeError && err.message === "Failed to fetch";

// A hung-but-abortable /me (TimeoutError from AbortSignal.timeout) is transient
// like a network error: retry within budget, never clear tokens.
const isTransientError = (err: unknown): boolean =>
  isNetworkError(err) || isTimeoutAbort(err);

const fetchCurrentUserOnce = async (
  token: string,
  uid: string | undefined,
  scheme: AuthScheme
): Promise<Response> =>
  fetch(`${viteEnv.VITE_SUNO_API}/auth/users/me/`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: buildAuthHeaderValue(token, scheme, uid)
    },
    // undefined when the flag is off → no signal, today's exact behavior.
    signal: buildCriticalTimeoutSignal()
  });

const clearAuthStorage = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("expiration");
  localStorage.removeItem("authScheme");
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("authScheme");
  if (viteEnv.VITE_ENV !== "local") {
    datadogRum.clearUser();
    datadogRum.clearGlobalContext();
  }
};

export const fetchCurrentUser = createAsyncThunk<
  CurrentUser,
  UserFetchPayload,
  { rejectValue: RejectValueType }
>("auth/userByToken", async ({ token, uid }, thunkAPI) => {
  const scheme = (thunkAPI.getState() as RootState).auth.authScheme;
  for (let attempt = 0; attempt <= FETCH_USER_MAX_RETRIES; attempt++) {
    try {
      const response: any = await fetchCurrentUserOnce(token!, uid, scheme);
      let data: any = await response.json();
      if (response.status === 200) {
        if (viteEnv.VITE_ENV !== "local" && data.id) {
          datadogRum.setUser({
            id: data.id,
            name: data.first_name + " " + data.last_name,
            email: data.email,
            practice: data.practice?.id,
            practice_name: data.practice?.name
          });

          datadogRum.setGlobalContext({
            practice_id: data.practice?.id,
            practice_name: data.practice?.name
          });
        }
        return data;
      } else if (response.status === 401 || response.status === 403) {
        // Auth rejection — token is invalid, clear and stop
        clearAuthStorage();
        return thunkAPI.rejectWithValue({});
      } else {
        // Other server error (5xx etc) — clear and stop
        clearAuthStorage();
        return thunkAPI.rejectWithValue({});
      }
    } catch (err: any) {
      // Timeout aborts retry on a tighter budget than plain network errors.
      const maxRetries = isTimeoutAbort(err)
        ? FETCH_USER_ABORT_MAX_RETRIES
        : FETCH_USER_MAX_RETRIES;

      if (isTransientError(err) && attempt < maxRetries) {
        // Transient (network drop or hung-but-aborted /me) — retry after a
        // delay, DON'T clear tokens.
        console.warn(
          `fetchCurrentUser: transient error (attempt ${attempt + 1}/${maxRetries + 1}), retrying...`
        );
        await new Promise((r) => setTimeout(r, FETCH_USER_RETRY_DELAY_MS));
        continue;
      }

      // Non-transient error or retries exhausted
      if (isTransientError(err)) {
        // All retries failed on a transient error — don't nuke tokens. Note:
        // useAuth's module-level fetchFailedForToken guard blocks re-dispatch
        // for this token within the SPA session, so recovery requires a full
        // page refresh (which also re-reads the preserved tokens).
        console.error(
          "fetchCurrentUser: transient error after all retries, preserving tokens"
        );
        return thunkAPI.rejectWithValue({
          errorMessage:
            "Unable to reach the server. Please check your connection and try again."
        });
      }

      // Unexpected error — clear tokens as before
      clearAuthStorage();
      return thunkAPI.rejectWithValue({
        errorMessage:
          "Something went wrong. Please contact your support and describe your issue."
      });
    }
  }

  // Should not reach here, but handle gracefully
  clearAuthStorage();
  return thunkAPI.rejectWithValue({
    errorMessage:
      "Something went wrong. Please contact your support and describe your issue."
  });
});

// Shared client-side teardown: auth storage and the RTK Query cache. Identical
// for both schemes — only the server-side logout call differs.
const teardownClientSession = async (thunkAPI: {
  dispatch: (action: any) => void;
}) => {
  clearAuthStorage();
  // Dynamically import to avoid circular dependency
  const { emptySplitApi } = await import("services/empty-split-api");
  thunkAPI.dispatch(emptySplitApi.util.resetApiState());
};

export const logoutUser = createAsyncThunk<
  boolean,
  UserFetchPayload,
  { rejectValue: RejectValueType }
>("auth/logout", async ({ token }, thunkAPI) => {
  const scheme = (thunkAPI.getState() as RootState).auth.authScheme;
  try {
    removeSunoCommsCachedAuthData();
    // Bearer / break-glass sessions are not DRF tokens — the `/auth/token/logout/`
    // endpoint would reject them. Tear down client-side only.
    // TODO(T5/backend): revoke the refresh token server-side for refresh-cookie
    // Bearer sessions (break-glass is access-only, so nothing to revoke there).
    if (scheme === "Bearer") {
      await teardownClientSession(thunkAPI);
      return true;
    }

    const response = await fetch(
      `${viteEnv.VITE_SUNO_API}/auth/token/logout/`,
      {
        method: "POST",
        headers: {
          Authorization: buildAuthHeaderValue(token!, scheme)
        }
      }
    );
    if (response.status === 204 || response.status === 401) {
      await teardownClientSession(thunkAPI);
      return true;
    } else {
      return thunkAPI.rejectWithValue({});
    }
  } catch (err: any) {
    const errorMessage =
      "Something went wrong. Please contact your support and describe your issue.";
    return thunkAPI.rejectWithValue({ errorMessage });
  }
});

interface AuthState {
  user: CurrentUser;
  currentClinic: Clinic | null;
  currentRegion: Region | null;
  practice: Practice | null;
  numberOfClinics: number;
  hasServiceLocations: boolean;
  hasRegions: boolean;
  token: string;
  authScheme: AuthScheme;
  isInitialLoad: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string;
  ldIdentified: boolean;
  recordMetric: boolean;
  features?: PaywallFeaturesStatus;
  showFeatureDialog: Record<string, boolean>;
}

const initialState = {
  user: {
    id: PRE_AUTH_USER_ID,
    email: "",
    first_name: "",
    last_name: "",
    full_name: "",
    title: -1,
    suffix: "",
    fullName: "",
    default_clinic: null,
    practice: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    groups: [],
    photo: "",
    npi: "",
    native_pdf_tables_enabled: false
  },
  currentClinic: null,
  practice: null,
  numberOfClinics: 0,
  hasRegions: false,
  hasServiceLocations: false,
  token: "",
  authScheme: DEFAULT_AUTH_SCHEME,
  isInitialLoad: true,
  isFetching: false,
  isSuccess: false,
  isError: false,
  errorMessage: "",
  ldIdentified: false,
  recordMetric: false,
  currentRegion: null,
  features: {},
  showFeatureDialog: {}
} as AuthState;

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearState(state) {
      state.isFetching = false;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = "";
      state.isInitialLoad = false;
      state.recordMetric = false;
      state.features = {};
      state.showFeatureDialog = {};
    },
    setToken(state, action) {
      state.token = action.payload.token;
      // Default applied here (not at call sites) so existing `{ token }`-only
      // dispatchers stay on the unchanged Token path.
      state.authScheme = action.payload.scheme ?? DEFAULT_AUTH_SCHEME;
    },
    setNativePdfTablesEnabled(state, action) {
      state.user.native_pdf_tables_enabled = action.payload;
    },
    updateCurrentPractice(state, action) {
      const localStorageClinic = setupLocalStorageAPI("selectedClinic");

      const practice = {
        ...state.practice,
        ...state.currentClinic?.practice,
        ...action.payload
      };
      state.practice = practice;

      localStorageClinic.updateData({
        ...state.currentClinic,
        practice: practice
      });
    },
    setCurrentClinic(state, action) {
      const localStorageClinic = setupLocalStorageAPI("selectedClinic");

      state.currentClinic = action.payload.currentClinic;
      state.numberOfClinics = action.payload.numberOfClinics;
      state.hasServiceLocations = action.payload.hasServiceLocations;
      state.hasRegions = action.payload.hasRegions;
      state.recordMetric = true;

      const clinicToStore = {
        ...action.payload.currentClinic,
        practice: {
          ...state.practice,
          ...action.payload.currentClinic?.practice,
          id: state.user?.practice?.id
        }
      };
      localStorageClinic.updateData(clinicToStore);
    },
    setCurrentRegion(state, { payload }: PayloadAction<Region | null>) {
      const localStorageRegion = setupLocalStorageAPI("selectedRegion");

      state.currentRegion = payload;

      localStorageRegion.updateData(state.currentRegion);
    },
    setUserClinics(state, { payload }: PayloadAction<ClinicsResponse>) {
      state.user.clinics = payload;
    },
    setLDIdentified(state, action) {
      state.ldIdentified = action.payload;
    },
    clearRecordMetric(state) {
      state.recordMetric = false;
    },
    setFeatures(state, action) {
      state.features = action.payload;
      state.showFeatureDialog = {};
    },
    showFeatureDialog(state, action) {
      state.showFeatureDialog[action.payload ?? ""] = true;
    },
    hideFeatureDialog(state, action) {
      state.showFeatureDialog[action.payload ?? ""] = false;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(signupUser.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(signupUser.fulfilled, (state, action) => {
      state.token = action.payload.auth_token;
      state.authScheme = DEFAULT_AUTH_SCHEME;
      state.isFetching = false;
      state.isSuccess = true;
    });
    builder.addCase(signupUser.rejected, (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = action.payload?.errorMessage ?? "";
    });
    builder.addCase(resetUserPassword.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(resetUserPassword.fulfilled, (state) => {
      state.isFetching = false;
      state.isSuccess = true;
    });
    builder.addCase(resetUserPassword.rejected, (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = action.payload?.errorMessage ?? "";
    });
    builder.addCase(resetUserPasswordConfirm.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(resetUserPasswordConfirm.fulfilled, (state) => {
      state.isFetching = false;
      state.isSuccess = true;
    });
    builder.addCase(resetUserPasswordConfirm.rejected, (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = action.payload?.errorMessage ?? "";
    });
    builder.addCase(loginUser.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      state.token = action.payload.auth_token;
      state.authScheme = DEFAULT_AUTH_SCHEME;
      state.isFetching = false;
      state.isSuccess = true;
    });
    builder.addCase(clearSsoSession.fulfilled, (state) => {
      // Mirror storage cleanup in Redux so RequireAuth can't rehydrate.
      state.token = "";
      state.authScheme = DEFAULT_AUTH_SCHEME;
      state.isSuccess = false;
    });
    builder.addCase(logoutUser.rejected, (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.authScheme = DEFAULT_AUTH_SCHEME;
      state.user = initialState.user;
      state.practice = initialState.practice;
      state.currentClinic = initialState.currentClinic;
      state.numberOfClinics = initialState.numberOfClinics;
      state.hasRegions = initialState.hasRegions;
      state.hasServiceLocations = initialState.hasServiceLocations;
      state.errorMessage = action.payload?.errorMessage ?? "";
      state.features = initialState.features;
      state.showFeatureDialog = initialState.showFeatureDialog;
    });
    builder.addCase(logoutUser.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.token = "";
      state.authScheme = DEFAULT_AUTH_SCHEME;
      state.user = initialState.user;
      state.practice = initialState.practice;
      state.currentClinic = initialState.currentClinic;
      state.numberOfClinics = initialState.numberOfClinics;
      state.hasRegions = initialState.hasRegions;
      state.hasServiceLocations = initialState.hasServiceLocations;
      state.isFetching = false;
      state.isSuccess = true;
      state.ldIdentified = false;
      state.features = initialState.features;
      state.showFeatureDialog = initialState.showFeatureDialog;
    });
    builder.addCase(loginUser.rejected, (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = action.payload?.errorMessage ?? "";
      state.ldIdentified = false;
      state.features = initialState.features;
      state.showFeatureDialog = initialState.showFeatureDialog;
    });
    builder.addCase(fetchCurrentUser.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(fetchCurrentUser.fulfilled, (state, action) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.user = formatStaffMemberFullName({
        ...action.payload,
        default_region: action.payload.default_clinic?.region,
        default_clinic:
          action.payload.default_clinic &&
          formatClinic(action.payload.default_clinic)
      });
      state.practice = action.payload.practice;
    });
    builder.addCase(fetchCurrentUser.rejected, (state, action) => {
      const isNetworkFailure =
        action.payload?.errorMessage ===
        "Unable to reach the server. Please check your connection and try again.";

      state.isFetching = false;
      state.errorMessage = action.payload?.errorMessage ?? "";

      if (isNetworkFailure) {
        // Network error after retries — preserve token and auth state
        // so the user can retry on next navigation or page refresh
        state.isError = true;
      } else {
        // Auth rejection or unexpected error — full reset
        state.isError = true;
        state.token = "";
        state.authScheme = DEFAULT_AUTH_SCHEME;
        state.user = initialState.user;
        state.practice = initialState.practice;
        state.currentClinic = initialState.currentClinic;
        state.numberOfClinics = initialState.numberOfClinics;
        state.hasRegions = initialState.hasRegions;
        state.hasServiceLocations = initialState.hasServiceLocations;
        state.ldIdentified = false;
        state.features = initialState.features;
        state.showFeatureDialog = initialState.showFeatureDialog;
      }
    });
  }
});

export const {
  clearState,
  setToken,
  setNativePdfTablesEnabled,
  setCurrentClinic,
  setCurrentRegion,
  setUserClinics,
  setLDIdentified,
  updateCurrentPractice,
  clearRecordMetric,
  setFeatures,
  showFeatureDialog,
  hideFeatureDialog
} = authSlice.actions;
export const authSelector = (state: RootState) => state.auth;
export const selectCurrentClinic = createSelector(
  authSelector,
  (auth) => auth.currentClinic
);
export const selectCurrentPractice = createSelector(
  authSelector,
  (auth) => auth.practice
);
export const selectRecordMetric = createSelector(
  authSelector,
  (auth) => auth.recordMetric
);
export const patientSettingsSelector = createSelector(
  selectCurrentPractice,
  (practice) => practice?.patient_settings
);
export const selectNumberOfActualClinics = createSelector(
  authSelector,
  (auth) => auth.numberOfClinics
);
export const selectNumberOfPracticeClinics = createSelector(
  authSelector,
  (auth) =>
    Boolean(auth.practice?.limit_to_current_clinic) ? 1 : auth.numberOfClinics
);
export const selectHasServiceLocations = createSelector(
  authSelector,
  (auth) => auth.hasServiceLocations
);
export const selectHasRegions = createSelector(
  authSelector,
  (auth) => auth.hasRegions
);
export const selectCurrentUser = createSelector(
  authSelector,
  (auth) => auth.user
);

export const selectCurrentUserToken = createSelector(
  authSelector,
  (auth) => auth.token
);

export const selectAuthScheme = createSelector(
  authSelector,
  (auth) => auth.authScheme
);

export const selectUserClinicsIds = createSelector(
  authSelector,
  (auth) => auth.user.clinics?.map(({ id }) => id) ?? []
);

export const selectCurrentRegion = createSelector(
  authSelector,
  (auth) => auth.currentRegion
);

export const selectCurrentRegionClinicsIds = createSelector(
  authSelector,
  (auth) => auth.currentRegion?.clinics?.map(({ id }) => id) ?? []
);

export const selectLDIdentified = createSelector(
  authSelector,
  (auth) => auth.ldIdentified
);

export const selectFeatures = createSelector(
  authSelector,
  (auth) => auth.features
);

export const selectShowFeatureDialog = createSelector(
  authSelector,
  (auth) => auth.showFeatureDialog
);

export const selectAuthErrorMessage = createSelector(
  authSelector,
  (auth) => auth.errorMessage
);

export const selectAuthIsFetching = createSelector(
  authSelector,
  (auth) => auth.isFetching
);

export const selectAuthIsSuccess = createSelector(
  authSelector,
  (auth) => auth.isSuccess
);

export const selectAuthIsError = createSelector(
  authSelector,
  (auth) => auth.isError
);

export const selectAuthIsInitialLoad = createSelector(
  authSelector,
  (auth) => auth.isInitialLoad
);

export default authSlice.reducer;
